import { defineStore } from 'pinia'
import axios from 'axios'

export const useChannelStore = defineStore('useChannelStore', {
    state: () => ({
        admins_count: 0,
        channels: [],
        currentChannel: null,
        currentChannelDetails: null,
        orgOptions: {},
        orgSettings: {},
        orgGroups: [],
        joinGroups: [],
        isLoaded: false,
        isLoading: false,
        isFetchingDetails: false,
        error: null,
    }),

    getters: {
        /**
         * Get all channels/organizations
         */
        allChannels: (state) => state.channels,

        /**
         * Get the current active channel/organization
         */
        activeChannel: (state) => state.currentChannel,

        /**
         * Get current channel ID
         */
        currentChannelId: (state) => state.currentChannel?.id || null,

        /**
         * Get current channel name
         */
        currentChannelName: (state) => state.currentChannel?.name || '',

        /**
         * Get current channel admins count
         */
        currentChannelAdminsCount: (state) => state.admins_count || 0,

        /**
         * Get current channel logo
         */
        currentChannelLogo: (state) => state.currentChannel?.logo || state.currentChannel?.img || '',

        /**
         * Check if user has multiple channels
         */
        hasMultipleChannels: (state) => state.channels.length > 1,

        /**
         * Get channel by ID
         */
        getChannelById: (state) => (id) => state.channels.find(channel => channel.id === id),

        /**
         * Get current organization options
         */
        currentOrgOptions: (state) => state.orgOptions,

        /**
         * Get current organization settings
         */
        currentOrgSettings: (state) => state.orgSettings,

        /**
         * Check if current channel is MarketScale Origin (id = -1)
         */
        isMarketScaleOrigin: (state) => state.currentChannel?.id === -1,

        /**
         * Get full channel details (from API)
         */
        channelDetails: (state) => state.currentChannelDetails,
    },

    actions: {
        /**
         * Initialize channels from props and fetch additional details from API
         * @param {Object} props - Props object containing orgs, orgOptions, orgSettings, etc.
         */
        async init(props = {}) {
            if (this.isLoaded || this.isLoading) {
                return
            }

            this.isLoading = true
            this.error = null

            try {
                this.channels = props.orgs || []
                this.currentChannel = this.channels.find(channel => channel.current) || null
                this.orgOptions = props.orgOptions || {}
                this.orgSettings = props.orgSettings || {}
                this.orgGroups = props.orgGroups || []
                this.joinGroups = props.joinGroups || []
                this.isLoaded = true

                // Fetch additional channel details from API
                if (this.currentChannel?.id && this.currentChannel.id !== -1) {
                    await this.fetchChannelDetails()
                }
            } catch (error) {
                console.error('Failed to initialize channel store:', error)
                this.error = error.message || 'Failed to load channel data'
            } finally {
                this.isLoading = false
            }
        },

        /**
         * Fetch detailed channel information from API
         */
        async fetchChannelDetails() {
            if (this.isFetchingDetails) {
                return
            }

            this.isFetchingDetails = true

            try {
                const response = await axios.get('/api/channel')

                if (response.data?.success && response.data?.channel) {
                    const apiChannel = response.data.channel
                    this.currentChannelDetails = apiChannel

                    // Get channel admins count
                    await this.getChannelAdminsCount()

                    // Merge API data into currentChannel
                    if (this.currentChannel) {
                        this.currentChannel = {
                            ...this.currentChannel,
                            ...apiChannel,
                            current: true, // Preserve the current flag
                        }

                        // Also update the channel in the channels array
                        const index = this.channels.findIndex(c => c.id === this.currentChannel.id)
                        if (index !== -1) {
                            this.channels[index] = this.currentChannel
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to fetch channel details:', error)
                // Don't set error state for details fetch failure - it's not critical
            } finally {
                this.isFetchingDetails = false
            }
        },

        /**
         * Update channels from fresh props
         * @param {Object} props - Props object containing orgs, orgOptions, orgSettings, etc.
         */
        refreshFromProps(props = {}) {
            this.channels = props.orgs || []
            this.currentChannel = this.channels.find(channel => channel.current) || null
            this.orgOptions = props.orgOptions || {}
            this.orgSettings = props.orgSettings || {}
            this.orgGroups = props.orgGroups || []
            this.joinGroups = props.joinGroups || []
        },

        /**
         * Set current channel by ID and fetch fresh details
         * @param {number} channelId - The channel/organization ID
         */
        async setCurrentChannel(channelId) {
            const channel = this.channels.find(c => c.id === channelId)
            if (channel) {
                // Mark all channels as not current
                this.channels.forEach(c => c.current = false)
                // Mark selected channel as current
                channel.current = true
                this.currentChannel = channel
                this.currentChannelDetails = null

                // Fetch new channel details
                if (channelId !== -1) {
                    await this.fetchChannelDetails()
                }
            }
        },

        /**
         * Get all MS Admins + Admins on the channel
         */
        getChannelAdminsCount() {
            return axios.get('/api/channels/'+this.currentChannelId+'/admins')
                .then(response => {
                    this.admins_count = response.data.count
                    return response.data.count
                })
                .catch(error => {
                    console.error('Failed to get channel admins count:', error)
                    return 0
                })
        },

        /**
         * Set channels manually
         */
        setChannels(channels) {
            this.channels = channels
            this.currentChannel = channels.find(channel => channel.current) || null
        },

        /**
         * Update organization options
         */
        setOrgOptions(options) {
            this.orgOptions = options
        },

        /**
         * Update organization settings
         */
        setOrgSettings(settings) {
            this.orgSettings = settings
        },

        /**
         * Clear channel data (logout)
         */
        clearChannels() {
            this.channels = []
            this.currentChannel = null
            this.currentChannelDetails = null
            this.orgOptions = {}
            this.orgSettings = {}
            this.orgGroups = []
            this.joinGroups = []
            this.isLoaded = false
        },
    }
})
